import Vue from 'vue';

import {IShipmentStore, IActionsSignature, IHub, initialHub} from "@/models/StoreModels";
import {IAmazonReturnsApi, ICrosstownApi} from "@/models/ApiModels";
import {i18n} from "@/main";
import {IPhoneNumber} from "@/models/IPhoneNumber";
import {StoreCrossTownDataIsReadyToSend} from '@/models/specifications/StoreCrossTownDataIsReadyToSend';
import {StoreAmazonDataIsReadyToSend} from '@/models/specifications/StoreAmazonDataIsReadyToSend';
import * as services from "@/services/services";
import {CELL_SIZES, EClickCollectType} from '@/constants/orders';


const storeCrossTownDataIsReadyToSend = new StoreCrossTownDataIsReadyToSend();
const storeAmazonDataIsReadyToSend = new StoreAmazonDataIsReadyToSend();

const initialState = (): IShipmentStore => {
  return {
    // crosstown
    to_phone_number_extension: "",
    to_phone_number: "",
    cell_size: CELL_SIZES.UNDEFINED_SIZE,
    from_hub_id: 0,
    note: "",

    // Click & Collect
    cc_type: EClickCollectType.CLIENT,
    cc_phone_number: "",
    cc_name: "",
    cc_shop_address: "",
    cc_comments: "",
    cc_hub: initialHub(),
  };
};


const shipmentGetters = {
  // crosstown
  getToPhoneNumber(state: IShipmentStore): IPhoneNumber {
    return {
      number: state.to_phone_number,
      extension: state.to_phone_number_extension,
    };
  },

  getFromHubId(state: IShipmentStore): number {
    return state.from_hub_id;
  },

  getCellSize(state: IShipmentStore): number {
    return state.cell_size;
  },

  getSizeHumanReadable(state: IShipmentStore): string {
    return state.cell_size === CELL_SIZES.SIZE_ID_SMALL  ? i18n.t("cells.cellSizeSmall") as string :
           state.cell_size === CELL_SIZES.SIZE_ID_LARGE  ? i18n.t("cells.cellSizeLarge") as string :
           state.cell_size === CELL_SIZES.SIZE_ID_MEDIUM ? i18n.t("cells.cellSizeMedium") as string :
                                                                     "";
  },

  getNote(state: IShipmentStore): string {
    return state.note;
  },

  getToPhoneNumberSpaces(state: IShipmentStore): string {
    let phoneSpaces: string = "";
    if (state.to_phone_number !== "") {
      const phoneSpacesArr: string[]|null = state.to_phone_number.match(/.{1,3}/g);
      phoneSpaces = phoneSpacesArr ? phoneSpacesArr.join(" ") : "";
    }

    return phoneSpaces;
  },

  getFormattedToPhoneNumber(state: IShipmentStore, getters: {[key: string]: any}): string {
    const phoneNumber = getters.getToPhoneNumberSpaces;
    if (phoneNumber !== "") {
      return `${state.to_phone_number_extension} ${phoneNumber}`;
    }

    return "";
  },

  crosstownDataIsCorrect(state: IShipmentStore): boolean {
    return storeCrossTownDataIsReadyToSend.IsSatisfiedBy(state);
  },

  amazonReturnsDataIsCorrect(state: IShipmentStore): boolean {
    return storeAmazonDataIsReadyToSend.IsSatisfiedBy(state);
  },

  getCrosstownShipmentBody(state: IShipmentStore): ICrosstownApi {
    return {
        to_phone_number: state.to_phone_number_extension + state.to_phone_number,
        cell_size_id: state.cell_size,
        from_hub_id: state.from_hub_id,
        note: state.note,
    };
  },

  getAmazonReturnsBody(state: IShipmentStore): IAmazonReturnsApi {
    return {
        cell_size_id: state.cell_size,
        from_hub_id: state.from_hub_id,
        note: state.note,
    };
  },

  // click and collect
  getCCType(state: IShipmentStore): EClickCollectType {
    return state.cc_type;
  },


  getCCPhoneNumber(state: IShipmentStore): string {
    return state.cc_phone_number;
  },

  getCCName(state: IShipmentStore): string {
    return state.cc_name;
  },

  getCCShopAddress(state: IShipmentStore): string {
    return state.cc_shop_address;
  },

  getCCSComments(state: IShipmentStore): string {
    return state.cc_comments;
  },

  getCCHub(state: IShipmentStore): IHub {
    return state.cc_hub;
  },
};


const actions = {
  reset({commit}: IActionsSignature<IShipmentStore>) {
    commit('reset');
  },


  setToPhoneNumber({commit, state}: IActionsSignature<IShipmentStore>, toPhoneNumber: IPhoneNumber) {
    toPhoneNumber.number = toPhoneNumber.number.replace(/\s/g, '');
    commit('setToPhoneNumber', toPhoneNumber);
  },


  setCellSize({commit}: IActionsSignature<IShipmentStore>, cellSize: string) {
    commit('setCellSize', cellSize);
  },


  setFromHubId({commit}: IActionsSignature<IShipmentStore>, fromHubId: string) {
    commit('setFromHubId', fromHubId);
  },


  setNotes({commit}: IActionsSignature<IShipmentStore>, note: string) {
    commit('setNotes', note);
  },

  createCrosstownShipment({commit, state, getters}: IActionsSignature<IShipmentStore>) {
    const body = getters.getCrosstownShipmentBody;

    return services.apiCaller.POST("/shipments/crosstown", body);
  },


  createAmazonReturnsShipment({commit, state, getters}: IActionsSignature<IShipmentStore>) {
    const body = getters.getAmazonReturnsBody;
    return services.apiCaller.POST("/shipments/amazonReturns", body);
  },

  // click and collect

  setCCType({commit, state}: IActionsSignature<IShipmentStore>, el: EClickCollectType) {
    commit('setCCType', el);
  },

  setCCPhoneNumber({commit, state}: IActionsSignature<IShipmentStore>, el: string) {
    commit('setCCPhoneNumber', el);
  },

  setCCName({commit, state}: IActionsSignature<IShipmentStore>, el: string) {
    commit('setCCName', el);
  },

  setCCShopAddress({commit, state}: IActionsSignature<IShipmentStore>, el: string) {
    commit('setCCShopAddress', el);
  },

  setCCComments({commit, state}: IActionsSignature<IShipmentStore>, el: string) {
    commit('setCCComments', el);
  },

  setCCHub({commit, state}: IActionsSignature<IShipmentStore>, el: IHub) {
    commit('setCCHub', el);
  },

};


const mutations = {
  // crosstown
  setToPhoneNumber(state: IShipmentStore, toPhoneNumber: IPhoneNumber) {
    Vue.set(state, 'to_phone_number', toPhoneNumber.number);
    Vue.set(state, 'to_phone_number_extension', toPhoneNumber.extension);
  },

  setCellSize(state: IShipmentStore, cellSize: string) {
    Vue.set(state, 'cell_size', cellSize);
  },

  setFromHubId(state: IShipmentStore, fromHubId: string) {
    Vue.set(state, 'from_hub_id', fromHubId);
  },

  setNotes(state: IShipmentStore, note: string) {
    Vue.set(state, 'note', note);
  },

  reset(state: IShipmentStore) {
    state = Object.assign(state, initialState());
  },


  // click and collect

  setCCType(state: IShipmentStore, el: EClickCollectType) {
    Vue.set(state, 'cc_type', el);
  },

  setCCPhoneNumber(state: IShipmentStore, el: string) {
    Vue.set(state, 'cc_phone_number', el);
  },

  setCCName(state: IShipmentStore, el: string) {
    Vue.set(state, 'cc_name', el);
  },

  setCCShopAddress(state: IShipmentStore, el: string) {
    Vue.set(state, 'cc_shop_address', el);
  },

  setCCComments(state: IShipmentStore, el: string) {
    Vue.set(state, 'cc_comments', el);
  },

  setCCHub(state: IShipmentStore, el: IHub) {
    Vue.set(state, 'cc_hub', el);
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters: shipmentGetters,
  actions,
  mutations,
};
